import React, { useMemo, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import HotelForm from './HotelForm';
import SpaForm from './SpaForm';
import TourForm from './TourForm';
import TravelPackageForm from './TravelPackageForm';
import './OwnerRegistration.css';

const OwnerRegistration = () => {
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [ownerToken, setOwnerToken] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const steps = useMemo(() => ([
    { key: 'phone', label: 'Phone' },
    { key: 'otp', label: 'Verify' },
    { key: 'type', label: 'Type' },
    { key: 'form', label: 'Details' }
  ]), []);

  const activeIndex = useMemo(() => {
    if (step === 'success') return steps.length - 1;
    return Math.max(0, steps.findIndex((item) => item.key === step));
  }, [step, steps]);

  const resetMessages = () => {
    setError('');
    setMessage('');
  };

  const handleSendOtp = async (event) => {
    event.preventDefault();
    resetMessages();
    if (!phone.trim()) {
      setError('Please enter a phone number.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/public/owners/send-otp`, { phone });
      setMessage('OTP sent successfully.');
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    resetMessages();
    if (!otp.trim()) {
      setError('Please enter the OTP.');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/public/owners/verify-otp`, {
        phone,
        otp
      });
      setOwnerToken(response.data?.token || '');
      setMessage('Phone verified successfully.');
      setStep('type');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    resetMessages();
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/public/owners/send-otp`, { phone });
      setMessage('OTP resent successfully.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectType = (type) => {
    resetMessages();
    setSelectedType(type);
    setStep('form');
  };

  const handleFormSuccess = () => {
    resetMessages();
    setStep('success');
  };

  const handleBackToType = () => {
    resetMessages();
    setStep('type');
  };

  const handleRestart = () => {
    resetMessages();
    setStep('phone');
    setOtp('');
    setOwnerToken('');
    setSelectedType('');
  };

  return (
    <div className="owner-registration-page">
      <div className="owner-registration-shell">
        <div className="owner-registration-hero">
          <span className="owner-badge">List your property</span>
          <h1>Register your hotel, spa, tour, or travel package</h1>
          <p>Verify your phone, choose your listing type, and complete the details to go live.</p>
        </div>

        <div className="owner-registration-steps">
          {steps.map((item, index) => (
            <div
              key={item.key}
              className={`owner-step ${index === activeIndex ? 'active' : ''} ${index < activeIndex ? 'complete' : ''}`}
            >
              <span className="owner-step-index">{index + 1}</span>
              <span className="owner-step-label">{item.label}</span>
            </div>
          ))}
        </div>

        {error && <div className="owner-alert owner-alert-error">{error}</div>}
        {message && <div className="owner-alert owner-alert-success">{message}</div>}

        {step === 'phone' && (
          <div className="owner-card">
            <h2>Enter your phone number</h2>
            <p className="owner-card-subtitle">We will send an OTP via Fast2SMS for verification.</p>
            <form onSubmit={handleSendOtp} className="owner-form">
              <div className="owner-input-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  placeholder="e.g., 9876543210"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Sending...' : 'Send OTP'}
              </button>
            </form>
          </div>
        )}

        {step === 'otp' && (
          <div className="owner-card">
            <h2>Verify OTP</h2>
            <p className="owner-card-subtitle">Enter the OTP sent to <strong>{phone}</strong>.</p>
            <form onSubmit={handleVerifyOtp} className="owner-form">
              <div className="owner-input-group">
                <label>OTP *</label>
                <input
                  type="number"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value)}
                  placeholder="Enter 6-digit OTP"
                  required
                />
              </div>
              <div className="owner-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleResendOtp}
                  disabled={loading}
                >
                  Resend OTP
                </button>
                <button type="button" className="owner-link" onClick={handleRestart} disabled={loading}>
                  Change phone number
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 'type' && (
          <div className="owner-card">
            <h2>Select listing type</h2>
            <p className="owner-card-subtitle">Choose what you want to register.</p>
            <div className="owner-type-grid">
              <button type="button" className="owner-type-card" onClick={() => handleSelectType('hotel')}>
                <span className="owner-type-icon">🏨</span>
                <span className="owner-type-title">Hotel</span>
                <span className="owner-type-desc">Rooms, amenities, policies, and media.</span>
              </button>
              <button type="button" className="owner-type-card" onClick={() => handleSelectType('spa')}>
                <span className="owner-type-icon">💆</span>
                <span className="owner-type-title">Spa</span>
                <span className="owner-type-desc">Services, timings, amenities, and media.</span>
              </button>
              <button type="button" className="owner-type-card" onClick={() => handleSelectType('tour')}>
                <span className="owner-type-icon">🎫</span>
                <span className="owner-type-title">Tour</span>
                <span className="owner-type-desc">Tours & activities, options, schedule, and media.</span>
              </button>
              <button type="button" className="owner-type-card" onClick={() => handleSelectType('travel-package')}>
                <span className="owner-type-icon">✈️</span>
                <span className="owner-type-title">Travel Package</span>
                <span className="owner-type-desc">Holiday packages, itinerary, inclusions, variants, and media.</span>
              </button>
            </div>
          </div>
        )}

        {step === 'form' && (
          <div className="owner-form-section">
            <div className="owner-form-header">
              <div>
                <h2>
                  {selectedType === 'hotel' ? 'Hotel registration' : selectedType === 'spa' ? 'Spa registration' : selectedType === 'tour' ? 'Tour registration' : 'Travel Package registration'}
                </h2>
                <p>Fill in the complete information to submit your listing.</p>
              </div>
              <button type="button" className="btn btn-secondary" onClick={handleBackToType}>
                Change type
              </button>
            </div>
            <div className="owner-form-body">
              {selectedType === 'hotel' && (
                <HotelForm
                  mode="owner"
                  ownerToken={ownerToken}
                  ownerPhone={phone}
                  onSuccess={handleFormSuccess}
                  onCancel={handleBackToType}
                />
              )}
              {selectedType === 'spa' && (
                <SpaForm
                  mode="owner"
                  ownerToken={ownerToken}
                  ownerPhone={phone}
                  onSuccess={handleFormSuccess}
                  onCancel={handleBackToType}
                />
              )}
              {selectedType === 'tour' && (
                <TourForm
                  mode="owner"
                  ownerToken={ownerToken}
                  ownerPhone={phone}
                  onSuccess={handleFormSuccess}
                  onCancel={handleBackToType}
                />
              )}
              {selectedType === 'travel-package' && (
                <TravelPackageForm
                  mode="owner"
                  ownerToken={ownerToken}
                  ownerPhone={phone}
                  onSuccess={handleFormSuccess}
                  onCancel={handleBackToType}
                />
              )}
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="owner-card owner-success-card">
            <h2>Listing submitted</h2>
            <p>Your registration has been received. Our team will review and activate your listing soon.</p>
            <button type="button" className="btn btn-primary" onClick={handleRestart}>
              Register another listing
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerRegistration;
